import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

export interface CaseTeamMember {
  assignment_id: string;
  department: string;
  assignment_kind: 'Primary' | 'Covering';
  staff_user_id: string;
  staff_name: string;
  staff_email: string;
  assigned_on: string;
  covers_until: string | null;
  granted_reason: string | null;
  ended_on: string | null;
  is_current: boolean;
  granted_by: string | null;
  is_live: boolean;
  days_left: number | null;
}

export interface CaseTeam {
  team: CaseTeamMember[];
  /** Whether the viewer may change any of it — the route decides, not the tier. */
  may_assign: boolean;
  /** The closed status when the case has ended (Pending discharge, Discharged, Deceased, "Deceased, discharged"), else null. */
  case_ended: string | null;
}

export interface CaseManager {
  id: string;
  full_name: string;
  email: string;
  caseload: number;
  /** A case manager of the department. False for the Director, who belongs and is listed after them (082). */
  is_case_manager?: boolean;
  /** Not of the department: a caseload exception names them for this client, with its reason. Never offered elsewhere. */
  by_exception?: boolean;
  exception_reason?: string | null;
}

/** How a person is offered in the pick list: the label says what they are. */
export function caseManagerLabel(m: CaseManager): string {
  const load = `${m.caseload} ${m.caseload === 1 ? 'client' : 'clients'}`;
  if (m.by_exception) return `${m.full_name} — by exception for this client`;
  if (m.is_case_manager === false) return `${m.full_name} — Director, ${load}`;
  return `${m.full_name} — ${load}`;
}

/**
 * Who holds this case and who is covering it.
 *
 * Every case manager can see this; only the Director can change it, and
 * `may_assign` comes back from the same call rather than being guessed from
 * the viewer's tier — the Director of Social Work is tier 3 and the rota is
 * theirs, which no tier rule would get right.
 */
export function useCaseTeam(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'case-team'],
    queryFn: () => apiFetch<CaseTeam>(`/clients/${clientId}/case-team`, getToken),
    ...queryDefaults,
  });
}

/**
 * The pick list, asked for only once somebody opens the form. With a client, it
 * also carries anyone a caseload exception names for that client (082):
 * possible, marked, and never in the list for anyone else.
 */
export function useCaseManagers(department: string, enabled: boolean, clientId?: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['case-managers', department, clientId ?? null],
    queryFn: () =>
      apiFetch<{ case_managers: CaseManager[] }>(
        `/case-managers?department=${encodeURIComponent(department)}` +
          (clientId ? `&client_id=${encodeURIComponent(clientId)}` : ''),
        getToken,
      ),
    ...queryDefaults,
    staleTime: 5 * 60_000,
    enabled,
  });
}

/**
 * Every change to the team, through one mutation keyed by path.
 *
 * All five write the same two things — who holds the case and who is lent it —
 * and all five invalidate the same queries: the client (its header names the
 * case manager), the visits board (its cards are grouped by assignment) and
 * the caseload counts.
 */
function useTeamMutation<TBody>(clientId: string, path: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<CaseTeam & { tasks_moved?: number }>(`/clients/${clientId}/${path}`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['case-managers'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
      // A new primary takes the open tasks the old one left on this client (118): both people's Tasks move.
      void queryClient.invalidateQueries({ queryKey: ['toolbar'] });
      void queryClient.invalidateQueries({ queryKey: ['work'] });
    },
  });
}

export const useAssignCaseManager = (clientId: string) =>
  useTeamMutation<{ staff_user_id: string; department?: string; notes?: string }>(
    clientId,
    'assignment',
  );

export const useGrantCoverage = (clientId: string) =>
  useTeamMutation<{
    staff_user_id: string;
    department?: string;
    days: number;
    reason: string;
  }>(clientId, 'coverage');

export const useExtendCoverage = (clientId: string) =>
  useTeamMutation<{ assignment_id: string; days: number }>(clientId, 'coverage/extend');

export const useEndCoverage = (clientId: string) =>
  useTeamMutation<{ assignment_id: string }>(clientId, 'coverage/end');

/** Bulk assignment from the visits board's unassigned card. */
export function useBulkAssign() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { client_ids: string[]; staff_user_id: string; department?: string }) =>
      apiFetch<{ assigned: number; requested: number; not_visible: number }>(
        '/assignments/bulk',
        getToken,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['client'] });
      void queryClient.invalidateQueries({ queryKey: ['case-managers'] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
